import { useEffect, useState } from 'react';
import { createDevice, fetchTypeOptions, type TypeOption } from '../api/devices';
import type { Env, LifecycleStatus } from '../types';
import { LIFECYCLE_ORDER, LIFECYCLE_META } from './LifecycleBadge';

const STATUSES = ['active', 'inactive', 'maintenance', 'unavailable'] as const;

/**
 * Formulaire « Ajouter une borne » (operator+). Obligatoires : n° de serie + nom.
 * Le reste est optionnel — la borne peut etre declaree des sa reception, avant
 * meme sa premiere connexion (le firmware/OCPP arriveront tout seuls).
 */
export function AddDeviceModal({
  env,
  onClose,
  onCreated,
}: {
  readonly env: Env;
  readonly onClose: () => void;
  readonly onCreated: (sn: string, env: Env) => void;
}) {
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Obligatoires
  const [sn, setSn] = useState('');
  const [name, setName] = useState('');
  // Optionnels
  const [typeId, setTypeId] = useState('');
  const [status, setStatus] = useState('active');
  const [lifecycle, setLifecycle] = useState<LifecycleStatus>('in_stock');
  const [price, setPrice] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [hw, setHw] = useState('');
  const [mfg, setMfg] = useState('');

  useEffect(() => {
    fetchTypeOptions(env)
      .then((r) => setTypes(r.data))
      .catch(() => setTypes([]));
  }, [env]);

  // Echap ferme la fenetre.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sn.trim() || !name.trim()) {
      setError('Le n° de serie et le nom sont obligatoires.');
      return;
    }
    if ((lat.trim() === '') !== (lng.trim() === '')) {
      setError('Latitude et longitude doivent etre renseignees ensemble.');
      return;
    }
    setBusy(true);
    try {
      const r = await createDevice(env, {
        device_sn: sn.trim(),
        device_name: name.trim(),
        device_type_id: typeId ? Number(typeId) : null,
        device_status: status,
        lifecycle_status: lifecycle,
        device_price: price.trim() ? Number(price) : null,
        device_latitude: lat.trim() ? Number(lat) : null,
        device_longitude: lng.trim() ? Number(lng) : null,
        hardware_version: hw.trim() || null,
        manufacturing_date: mfg || null,
      });
      onCreated(r.data.deviceSn, r.data.env);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter une borne"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form onSubmit={submit} className="panel w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="panel-header">
          <h2 className="panel-title">Ajouter une borne</h2>
          <span className={env === 'prod' ? 'pill-env-prod' : 'pill-env-staging'}>
            <span className="pill-dot" />
            {env}
          </span>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[13px] t-muted">
            La borne sera creee dans la base <strong className="t-strong">{env}</strong> avec une entree «
            creation » dans sa fiche de vie. Champs marques <span className="text-red-400">*</span> obligatoires.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-sn" className="label">
                N° de serie <span className="text-red-400">*</span>
              </label>
              <input id="add-sn" value={sn} onChange={(e) => setSn(e.target.value)} className="input" required maxLength={100} placeholder="ex : ZCE-2026-00042" />
            </div>
            <div>
              <label htmlFor="add-name" className="label">
                Nom <span className="text-red-400">*</span>
              </label>
              <input id="add-name" value={name} onChange={(e) => setName(e.target.value)} className="input" required maxLength={255} placeholder="ex : Borne parking A3" />
            </div>
            <div>
              <label htmlFor="add-type" className="label">Type</label>
              <select id="add-type" value={typeId} onChange={(e) => setTypeId(e.target.value)} className="input">
                <option value="">— Non defini —</option>
                {types.map((t) => (
                  <option key={t.typeId} value={t.typeId}>
                    {t.typeName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="add-status" className="label">Statut</label>
              <select id="add-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input">
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="add-lifecycle" className="label">Etape de vie initiale</label>
              <select
                id="add-lifecycle"
                value={lifecycle}
                onChange={(e) => setLifecycle(e.target.value as LifecycleStatus)}
                className="input"
              >
                {LIFECYCLE_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {LIFECYCLE_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="add-price" className="label">Prix (EUR)</label>
              <input id="add-price" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="input" placeholder="ex : 1490.00" />
            </div>
            <div>
              <label htmlFor="add-lat" className="label">Latitude</label>
              <input id="add-lat" type="number" min="-90" max="90" step="any" value={lat} onChange={(e) => setLat(e.target.value)} className="input" placeholder="ex : 48.8566" />
            </div>
            <div>
              <label htmlFor="add-lng" className="label">Longitude</label>
              <input id="add-lng" type="number" min="-180" max="180" step="any" value={lng} onChange={(e) => setLng(e.target.value)} className="input" placeholder="ex : 2.3522" />
            </div>
            <div>
              <label htmlFor="add-hw" className="label">Version materielle</label>
              <input id="add-hw" value={hw} onChange={(e) => setHw(e.target.value)} className="input" maxLength={50} placeholder="ex : rev C" />
            </div>
            <div>
              <label htmlFor="add-mfg" className="label">Date de fabrication</label>
              <input id="add-mfg" type="date" value={mfg} onChange={(e) => setMfg(e.target.value)} className="input" />
            </div>
          </div>

          <p className="text-xs t-faint">
            Client, station, firmware et donnees OCPP ne se saisissent pas ici : ils sont renseignes par la
            plateforme wattelzce (attribution client, premiere connexion de la borne).
          </p>

          {error && <div className="pill pill-red !whitespace-normal py-1.5">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t bd">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>
            Annuler
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Creation...' : 'Creer la borne'}
          </button>
        </div>
      </form>
    </div>
  );
}
